/**
 * What a signed-out visitor's browser keeps between the journey's screens (2026-10-03).
 *
 * The journey is open until Build: an ad's visitor describes the app and gets a tailored plan
 * without an account, and is asked for one when they press Build (or attach a file). Two small
 * facts carry across that detour, both in this browser only and never sent to Meta:
 *
 *  - `anonymousId` — a random id the /plan server counts signed-out plans by (with the IP), so a
 *    capped free plan cannot be had again just by clearing nothing. Not a person, not stored with
 *    anything they typed, and not used once they have an account.
 *  - `buildAfterSignUp` — that the account was asked for BY Build, so the plan screen goes straight
 *    on to the trial offer when they are back, instead of making them press Build a second time.
 *    sessionStorage: per tab, and gone with it.
 */
const ID_KEY = 'saddle.anonId';
const BUILD_KEY = 'saddle.buildAfterSignUp';

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

export function anonymousId(): string {
  try {
    const kept = localStorage.getItem(ID_KEY);
    if (kept && /^[A-Za-z0-9-]{8,64}$/.test(kept)) return kept;
    const made = newId();
    localStorage.setItem(ID_KEY, made);
    return made;
  } catch {
    // Storage refused (a private window): a fresh id per page, which the IP cap still bounds.
    return newId();
  }
}

/**
 * Take on another browser's id: the one an "email me my app link" email names (api/emailLink.ts),
 * so the build it began signed out is this browser's to collect at sign-up.
 */
export function adoptAnonymousId(id: string): void {
  if (!/^[A-Za-z0-9-]{8,64}$/.test(id)) return;
  try {
    localStorage.setItem(ID_KEY, id);
  } catch {
    /* Storage refused: the claim at sign-up then has no id, and the build is not collected here. */
  }
}

export function rememberBuildAfterSignUp(): void {
  try {
    sessionStorage.setItem(BUILD_KEY, String(Date.now()));
  } catch {
    /* without it they press Build once more, which still works */
  }
}

/** True once, within an hour of the Build press that asked for the account; then forgotten. */
export function takeBuildAfterSignUp(): boolean {
  try {
    const at = Number(sessionStorage.getItem(BUILD_KEY));
    sessionStorage.removeItem(BUILD_KEY);
    return Number.isFinite(at) && at > 0 && Date.now() - at < 60 * 60 * 1000;
  } catch {
    return false;
  }
}
